import type {CashbackCampaign} from '@/modules/common/hooks/useCampaigns/types';
import type {Breakpoint as BreakpointType} from '@travel-frontend/themes';

import {
  Breakpoint,
  useBreakpointDown,
  useBreakpointUp,
} from '@travel-frontend/core';
import {IconChevronRight} from '@travel-frontend/icons';
import {
  CashbackInterceptor,
  CashbackInterceptorSize,
  SPACING_NONE,
  SPACING_XSMALL,
  SPACING_SMALL,
  SPACING_MEDIUM,
  SPACING_XLARGE,
  Stack,
  useBooleanToggle,
} from '@travel-frontend/ui-components';
import {useCallback, useMemo} from 'react';

import {Background} from '@/components/CampaignBanners/components/Background';
import {Container} from '@/components/CampaignBanners/components/Container';
import {HintElement} from '@/components/CampaignBanners/components/HintElement';
import {OverlayTrigger} from '@/components/CampaignBanners/components/OverlayTrigger';
import {Subtitle} from '@/components/CampaignBanners/components/Subtitle';
import {TextWrapper} from '@/components/CashbackCampaignBanner/components/TextWrapper';
import {Title} from '@/components/CashbackCampaignBanner/components/Title';
import {ConditionsLayer} from '@/components/ConditionsLayer';
import {useTracking} from '@/modules/common/hooks/useTracking';

type Props = CashbackCampaign & {position: number};

export const CashbackCampaignBanner: React.FunctionComponent<Props> = ({
  conditionsId,
  gradient,
  interceptorHighlight,
  interceptorText,
  link,
  subtitle,
  title,
  hint,
  hintTooltip,
  position,
}) => {
  const [isOpen, setIsOpen] = useBooleanToggle();
  const {dispatchGenericTrackingEvent} = useTracking();
  const handleOpen = useCallback(() => setIsOpen(true), [setIsOpen]);
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const smDown = useBreakpointDown('sm');
  const lgUp = useBreakpointUp('lg');

  const handleOnClick = useCallback(() => {
    handleOpen();
    dispatchGenericTrackingEvent({
      event: 'promotion_banner',
      event_action: 'cashback_click',
      ad_position: `${position}`,
      non_interaction: false,
    });
  }, [handleOpen, dispatchGenericTrackingEvent, position]);

  const textElements = useMemo(
    () =>
      (['xs', 'sm', 'md', 'lg', 'xl'] as Array<BreakpointType>).map((bp) => {
        const titleText = title[bp];
        const subtitleText = subtitle[bp];
        const linkText = link[bp];
        const spacing = lgUp ? SPACING_NONE : SPACING_XSMALL;

        return (
          <Breakpoint key={bp} matches={[bp]}>
            <TextWrapper>
              {titleText && <Title>{titleText}</Title>}
              {(subtitleText || linkText) && (
                <Stack direction="column" spacing={spacing}>
                  {subtitleText && <Subtitle value={subtitleText} />}
                  {linkText && (
                    <OverlayTrigger type="button" onClick={handleOnClick}>
                      {linkText}
                      <IconChevronRight />
                    </OverlayTrigger>
                  )}
                </Stack>
              )}
            </TextWrapper>
          </Breakpoint>
        );
      }),
    [title, subtitle, link, lgUp, handleOnClick]
  );

  const [from, to] = gradient.split(',');
  const containerSpacing = smDown ? SPACING_MEDIUM : SPACING_XLARGE;
  const InterceptorSize = smDown
    ? CashbackInterceptorSize.MEDIUM
    : CashbackInterceptorSize.LARGE;

  const hasHintOrInterceptor =
    (interceptorText && interceptorHighlight) || (hint && hintTooltip);

  return (
    <Container
      gradient={gradient}
      direction="row"
      alignItems="center"
      spacing={containerSpacing}
      justifyContent="start"
    >
      <Background from={from} to={to} />
      {hasHintOrInterceptor && (
        <Stack alignItems="center" spacing={SPACING_SMALL} flexShrink={0}>
          <CashbackInterceptor
            text={interceptorText}
            highlight={interceptorHighlight}
            size={InterceptorSize}
          />
          <HintElement hint={hint} hintTooltip={hintTooltip} />
        </Stack>
      )}
      {textElements}
      <ConditionsLayer
        identifier={conditionsId}
        open={isOpen}
        onClose={handleClose}
      />
    </Container>
  );
};
